import { supabase } from './supabase'
import { isSpreadsheetName, openXlsxPreview } from './xlsxPreview'
import { isImageName, openImagePreview } from './imagePreview'

// Fetches a fresh 10-minute signed R2 URL for a document. Per CLAUDE.md, signed
// URLs are per-document, per-request, and never cached client-side. Server-side
// signing only — we POST the document id, the edge function checks RLS, then
// returns the URL.
export async function getSignedUrl(documentId: string): Promise<string | null> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) return null
  const res = await fetch(
    `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/documents-sign-preview`,
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ documentId }),
    },
  )
  if (!res.ok) return null
  const body = (await res.json()) as { url?: string }
  return body.url ?? null
}

// Fetches the document's raw BYTES via the edge function (which proxies R2 with
// CORS). Used by the in-app xlsx viewer — a direct cross-origin fetch of the
// signed R2 URL is CORS-blocked, but the function response carries the right
// Access-Control headers.
// `preferPreview` fetches the browser-VIEWABLE derivative (HEIC/TIFF → JPEG) when one
// exists — required by the crop tool's canvas, which cannot decode a HEIC/TIFF original.
export async function fetchDocumentBytes(
  documentId: string,
  opts?: { preferPreview?: boolean },
): Promise<ArrayBuffer | null> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) return null
  const res = await fetch(
    `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/documents-sign-preview`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ documentId, raw: true, preview: opts?.preferPreview === true }),
    },
  )
  if (!res.ok) return null
  return res.arrayBuffer()
}

// Opens a document's signed URL in a new browser tab.
export async function openSignedPreview(documentId: string): Promise<void> {
  const url = await getSignedUrl(documentId)
  if (url) window.open(url, '_blank', 'noopener')
}

// "Ver documento" entry point. Branches:
//   - Link-only docs (email-body download link, no attachment) → open the link.
//   - Spreadsheets (.xlsx/.xls) → in-app viewer (a browser tab can't render xlsx,
//     and we keep the ORIGINAL sheet as the stored file — lossless, no PDF).
//   - Images → in-app viewer WITH ZOOM. A bare browser tab fits an image to the window
//     and gives no zoom affordance, so a photographed invoice was unreadable; PDFs never
//     had this problem because the browser's PDF plugin ships its own zoom.
//   - Everything else (PDF, unknown) → signed-preview in a new tab, unchanged.
export function openDocument(doc: {
  id: string
  source_url?: string | null
  filename_original?: string | null
  // Most callers pass the whole row, so the printed invoice number rides along for free and the
  // in-app image viewer can title the document by what it IS, not just what the file is called.
  // The index signature keeps narrow extraction shapes (Extratos' `{ bankName }`) assignable —
  // TS's weak-type check rejects an object with no properties in common otherwise.
  extraction?: { documentNumber?: string | null; [key: string]: unknown } | null
}): void {
  if (doc.source_url) {
    window.open(doc.source_url, '_blank', 'noopener')
    return
  }
  if (isSpreadsheetName(doc.filename_original)) {
    openXlsxPreview({ id: doc.id, filename: doc.filename_original ?? 'Folha de cálculo' })
    return
  }
  if (isImageName(doc.filename_original)) {
    openImagePreview({
      id: doc.id,
      filename: doc.filename_original ?? 'Imagem',
      documentNumber: doc.extraction?.documentNumber ?? null,
    })
    return
  }
  void openSignedPreview(doc.id)
}

// Saves a document to the user's machine under its ORIGINAL filename.
//
// Goes through the edge function's raw arm rather than the signed R2 URL on
// purpose: a signed URL opens in a tab (the browser decides, and for a PDF it
// always decides "display"), and `<a download>` is ignored cross-origin. Bytes
// + an object URL is the only way to make "Descarregar" actually download.
// Returns false so the caller can surface a failure instead of appearing to
// have done something.
export async function downloadDocument(
  documentId: string,
  filename?: string | null,
): Promise<boolean> {
  const bytes = await fetchDocumentBytes(documentId)
  if (!bytes) return false
  const url = URL.createObjectURL(new Blob([bytes]))
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename && filename.trim() !== '' ? filename : 'documento'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // Revoking synchronously can beat the download in Safari; one frame is
    // enough for the click to have been consumed.
    setTimeout(() => URL.revokeObjectURL(url), 1_000)
  }
  return true
}
